import { useState, useEffect, useMemo } from "react";
import { CampoNumero, Resultado, Dato, Rejilla, mxn } from "../components/campos.jsx";
import { calcularSueldoNeto } from "../lib/calc.js";
import { diasVacacionesPorAntiguedad, MINIMOS_LFT, UMA } from "../data/fiscal.js";

export default function SueldoNeto() {
  const [bruto, setBruto] = useState(18000);
  const [anios, setAnios] = useState(1);
  const [vacaciones, setVacaciones] = useState(12);
  const [prima, setPrima] = useState(MINIMOS_LFT.primaVacacionalPct);
  const [aguinaldo, setAguinaldo] = useState(MINIMOS_LFT.diasAguinaldo);

  // Los días de vacaciones siguen a la antigüedad, pero se pueden
  // sobrescribir: hay contratos que dan más que el mínimo de ley.
  useEffect(() => {
    setVacaciones(diasVacacionesPorAntiguedad(anios));
  }, [anios]);

  const r = useMemo(
    () =>
      calcularSueldoNeto({
        sueldoMensual: bruto,
        aniosAntiguedad: anios,
        diasVacaciones: vacaciones,
        primaVacacionalPct: prima,
        diasAguinaldo: aguinaldo,
      }),
    [bruto, anios, vacaciones, prima, aguinaldo],
  );

  return (
    <div className="space-y-6">
      <Rejilla>
        <div className="space-y-4">
          <CampoNumero label="Sueldo bruto mensual" valor={bruto} onChange={setBruto} prefijo="$" min={0} />
          <CampoNumero
            label="Años de antigüedad"
            valor={anios}
            onChange={setAnios}
            min={1}
            max={50}
            step="1"
            ayuda="Determina tus días de vacaciones según la LFT."
          />
          <div className="grid grid-cols-2 gap-3">
            <CampoNumero label="Días vacaciones" valor={vacaciones} onChange={setVacaciones} min={0} step="1" />
            <CampoNumero label="Prima vac." valor={prima} onChange={setPrima} sufijo="%" min={25} />
          </div>
          <CampoNumero
            label="Días de aguinaldo"
            valor={aguinaldo}
            onChange={setAguinaldo}
            min={15}
            step="1"
            ayuda="Mínimo de ley: 15 días."
          />
        </div>

        <div className="order-first space-y-3 lg:order-last lg:col-span-2">
          <Resultado
            label="Sueldo neto mensual (lo que recibes)"
            valor={mxn(r.neto)}
            tono="positivo"
            nota={`Retención efectiva: ${r.tasaEfectiva.toFixed(1)}% del bruto`}
          />

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="tarjeta">
              <h3 className="mb-2 text-2xs font-bold uppercase tracking-wider texto-3">
                Impuestos y cuotas
              </h3>
              <Dato label="Sueldo bruto" valor={mxn(r.bruto)} />
              {/* Sin subsidio, el ISR de tarifa y el ISR a cargo son el mismo
                  número: mostrarlo dos veces sólo confunde. */}
              {r.subsidio > 0 ? (
                <>
                  <Dato label={`ISR de tarifa (tramo ${r.tramoIsr?.tasa ?? 0}%)`} valor={`− ${mxn(r.isrBruto)}`} />
                  <Dato label="Subsidio para el empleo" valor={`+ ${mxn(r.subsidio)}`} />
                  <Dato label="ISR a cargo" valor={`− ${mxn(r.isr)}`} />
                </>
              ) : (
                <Dato label={`ISR (tramo ${r.tramoIsr?.tasa ?? 0}%)`} valor={`− ${mxn(r.isr)}`} />
              )}
              <Dato label="Cuotas IMSS (obrero)" valor={`− ${mxn(r.imss)}`} />
              <Dato label="Neto" valor={mxn(r.neto)} destacar />
            </div>

            <div className="tarjeta">
              <h3 className="mb-2 text-2xs font-bold uppercase tracking-wider texto-3">
                Salario Base de Cotización
              </h3>
              <Dato label="Salario diario" valor={mxn(r.salarioDiario)} />
              <Dato label="Factor de integración" valor={r.factorIntegracion.toFixed(4)} />
              <Dato label="SBC diario" valor={mxn(r.sbc)} destacar />
              <Dato label="Días de vacaciones" valor={`${r.diasVacaciones} días`} />
              <Dato label="UMA diaria vigente" valor={mxn(UMA.diaria)} />
            </div>
          </div>

          <details className="tarjeta">
            <summary className="cursor-pointer text-sm font-medium texto-2">
              Ver desglose de cuotas del IMSS
            </summary>
            <div className="mt-3">
              {r.imssDesglose.map((d) => (
                <Dato key={d.concepto} label={`${d.concepto} (${d.tasa}%)`} valor={mxn(d.importe)} />
              ))}
              <Dato label="Total cuotas obrero" valor={mxn(r.imss)} destacar />
            </div>
          </details>
        </div>
      </Rejilla>
    </div>
  );
}
